import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CellularBillingService } from 'src/app/shared/services/cellular-billing.service';
import { OverlayCustomCodeDialogComponent } from '../dialogs/overlay-custom-code-dialog.component';
import { OverlayCostComparisonDialogComponent } from '../dialogs/overlay-cost-comparison-dialog.component';
import { OverlayBillingRow } from 'src/app/shared/model/cellular-billing';

@Component({
  selector: 'app-overlay-usage-table', templateUrl: './overlay-usage-table.component.html',
  styleUrls: ['./overlay-usage-table.component.scss']
})
export class OverlayUsageTableComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() rows: OverlayBillingRow[] = [];
  @Input() readOnly = false;
  @Input() pricingRevision = 0;
  @Input() duplicatesOnly = false;
  @Output() duplicatesOnlyChange = new EventEmitter<boolean>();
  @Output() edited = new EventEmitter<void>();
  duplicateMacs: string[] = [];
  searchValue = '';
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  columns = ['select', 'hostname', 'mac', 'organization', 'dataUsageGb', 'bundleGb', 'gigsToCharge', 'bundle', 'msp', 'order', 'remarks'];
  readonly selection = new SelectionModel<OverlayBillingRow>(true, []);
  readonly data = new MatTableDataSource<OverlayBillingRow>([]);
  bulkGb: number = null;
  bundleCodes: string[] = [];
  codesError = '';
  pricingError = '';
  pricingBusy = false;
  private pricingRequest: Subscription;
  private readonly pricingEdits = new Subject<void>();
  private readonly subscriptions = new Subscription();

  constructor(private billing: CellularBillingService, private dialog: MatDialog) {
    this.subscriptions.add(this.pricingEdits.pipe(debounceTime(200)).subscribe(() => this.refreshPricing()));
    this.data.filterPredicate = row => (!this.duplicatesOnly || row.hasDuplicateMac) &&
      [row.mac, row.hostname, row.msp, row.organization, row.order, row.remarks].join(' ').toLowerCase().includes(this.searchValue.trim().toLowerCase());
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows) {
      this.data.data = (this.rows || []).filter(row => row.dataUsageGb > 14);
      this.duplicateMacs = Array.from(new Set(this.data.data.filter(row => row.hasDuplicateMac).map(row => row.mac).filter(Boolean))).sort();
      this.selection.clear();
      if (!this.readOnly) this.loadCodes();
    }
    this.columns = [...(this.readOnly ? [] : ['select']), 'hostname', 'mac', 'organization', 'dataUsageGb', 'bundleGb', 'gigsToCharge', 'bundle', 'msp', 'order', 'remarks'];
    this.columns.splice(this.columns.indexOf('bundle'), 0, 'partnerTier', 'recommendedCost');
    if (!this.readOnly && (changes.rows || changes.pricingRevision)) this.refreshPricing();
    this.refreshFilter();
  }
  ngAfterViewInit(): void { this.data.sort = this.sort; }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); if (this.pricingRequest) this.pricingRequest.unsubscribe(); }
  refreshPricing(): void {
    if (this.pricingRequest) this.pricingRequest.unsubscribe();
    this.pricingError = ''; this.pricingBusy = false;
    if (!this.rows.length || this.readOnly) return;
    const currentRows = this.rows;
    const previousPricing = new Map(currentRows.map(row => [row.rowId, row.pricing]));
    currentRows.forEach(row => row.pricing = undefined);
    this.pricingBusy = true;
    this.pricingRequest = this.billing.quoteOverlayRows(currentRows).subscribe(results => {
      if (this.rows !== currentRows) return;
      const byId = new Map(results.map(result => [result.rowId, result.pricing]));
      currentRows.forEach(row => {
        const pricing = byId.get(row.rowId);
        if (JSON.stringify(previousPricing.get(row.rowId)) !== JSON.stringify(pricing)) row.costComparison = undefined;
        row.pricing = pricing;
      });
      this.pricingBusy = false;
    }, () => { currentRows.forEach(row => row.costComparison = undefined); this.pricingBusy = false; this.pricingError = 'Unable to refresh pricing. Retry before relying on a recommendation.'; });
  }
  recommendedCodes(row: OverlayBillingRow): string {
    if (row.costComparison) return 'Selected · ' + (row.costComparison.selectedCode === 'OVERLAY_OVERAGE_1GB' ? 'Pay as you go' : row.costComparison.selectedCode);
    if (row.pricing && row.pricing.mode === 'NO_SUBSCRIPTION' && row.pricing.options && row.pricing.options.length) return 'No subscription · $' + (row.pricing.options[0].unitPriceCents / 100).toFixed(2) + '/GB';
    return row.pricing && row.pricing.options ? row.pricing.options.filter(option => option.lowest).map(option => option.code).join(' / ') : '';
  }
  displayCostCents(row: OverlayBillingRow): number {
    if (row.costComparison) {
      const selected = row.costComparison.quote.options.find(option => option.code === row.costComparison.selectedCode);
      if (selected) return selected.costCents;
    }
    return row.pricing && row.pricing.lowestCostCents;
  }
  loadCodes(): void {
    this.codesError = '';
    this.subscriptions.add(this.billing.listOverlayBundles().subscribe(records => {
      this.bundleCodes = Array.from(new Set([...records.map(record => record.code), ...this.rows.map(row => row.bundle)]))
        .filter(code => code && !['NO', 'NO_BILL', 'CONFLICT', 'YES'].includes(code)).sort();
    }, () => this.codesError = 'Unable to load bundle codes. Retry before choosing a registered code.'));
  }
  chooseCode(row: OverlayBillingRow, code: string, select?: MatSelect): void {
    if (code === '__CUSTOM__') {
      const ref = this.dialog.open(OverlayCustomCodeDialogComponent, { width: '480px', maxWidth: 'calc(100vw - 32px)' });
      this.subscriptions.add(ref.afterClosed().subscribe(value => {
        if (value) {
          if (!this.bundleCodes.includes(value) && !['NO', 'NO_BILL'].includes(value)) this.bundleCodes = [...this.bundleCodes, value].sort();
          this.chooseCode(row, value);
        }
        if (select) select.value = row.bundle;
      }));
      return;
    }
    row.bundle = code;
    if (row.xTimesRemainingUsage === undefined) row.xTimesRemainingUsage = 1;
    row.bundleSelection = 'MANUAL';
    row.bundleMapping = 'MANUAL';
    this.calculate(row);
  }
  useAutomatic(row: OverlayBillingRow): void {
    row.bundleSelection = 'AUTO';
    row.bundle = row.automaticBundle || 'NO';
    if (row.automaticBundleGb !== null && row.automaticBundleGb !== undefined) row.bundleGb = row.automaticBundleGb;
    row.bundleMapping = row.bundle === 'CONFLICT' ? 'AMBIGUOUS' : row.bundle === 'NO' ? 'NO_ACTIVE_BUNDLE' : 'MATCHED';
    this.calculate(row);
  }
  get validBulk(): boolean { return this.bulkGb !== null && Number.isFinite(Number(this.bulkGb)) && Number(this.bulkGb) >= 0; }
  applyBulk(): void {
    if (!this.validBulk) return;
    this.selection.selected.forEach(row => { row.bundleGb = Number(this.bulkGb); this.calculate(row); });
    this.selection.clear();
  }
  allVisibleSelected(): boolean { return !!this.data.filteredData.length && this.data.filteredData.every(row => this.selection.isSelected(row)); }
  toggleVisible(): void {
    const clear = this.allVisibleSelected();
    this.data.filteredData.forEach(row => clear ? this.selection.deselect(row) : this.selection.select(row));
  }
  calculate(row: OverlayBillingRow): void {
    row.costComparison = undefined;
    this.edited.emit();
    if (row.bundleMapping === 'AMBIGUOUS' || row.bundleGb === null || !Number.isFinite(Number(row.bundleGb)) || Number(row.bundleGb) < 0) {
      row.gigsToCharge = null; return;
    }
    const allowance = Number(row.bundleGb);
    row.gigsToCharge = row.bundle === 'NO_BILL' || row.dataUsageGb < allowance ? 0 : row.bundle === 'NO'
      ? Math.ceil(row.dataUsageGb * 0.95) - allowance : Math.ceil(row.dataUsageGb - allowance);
    if (this.pricingEdits) this.pricingEdits.next();
  }
  compareCosts(row: OverlayBillingRow): void {
    this.dialog.open(OverlayCostComparisonDialogComponent, { width: '720px', maxWidth: 'calc(100vw - 32px)', data: {
      ...row, pricingSnapshotOnly: this.readOnly,
      onComparisonChange: comparison => {
        if (this.readOnly || !this.rows.includes(row)) return;
        row.costComparison = comparison;
        this.edited.emit();
      }
    } });
  }
  hasBundleCode(row: OverlayBillingRow): boolean { return !!row.bundle && !['NO', 'NO_BILL', 'CONFLICT'].includes(row.bundle) && row.bundleMapping !== 'AMBIGUOUS'; }
  canCompareCosts(row: OverlayBillingRow): boolean { return (row.bundle === 'NO' || this.hasBundleCode(row)) && row.bundleMapping !== 'AMBIGUOUS' && typeof row.gigsToCharge === 'number' && Number.isFinite(row.gigsToCharge) && row.gigsToCharge > 0; }
  search(value: string): void { this.searchValue = value; this.refreshFilter(); }
  toggleDuplicates(value: boolean): void {
    this.duplicatesOnly = value;
    this.duplicatesOnlyChange.emit(value);
    this.refreshFilter();
  }
  viewDuplicateMac(mac: string): void { this.searchValue = mac; this.toggleDuplicates(true); }
  private refreshFilter(): void { this.data.filter = JSON.stringify({ search: this.searchValue, duplicatesOnly: this.duplicatesOnly }); }
}
